// Libraries
import React from 'react';

// Supermove

import {Icon} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useQuery, useToast, useEffect} from '@supermove/hooks';
import {LeadProviderEndpoint} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import ActionModal from '@shared/design/components/Modal/SmallModal/ActionModal';
import Toast from '@shared/design/components/Toast';
import ErrorToast from '@shared/design/components/Toast/ErrorToast';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import useUpdateLeadProviderEndpointMutation from 'modules/Organization/Settings/Company/components/LeadProvider/useUpdateLeadProviderEndpointMutation';

const UpdateLeadProviderEndpointStatusModal = ({
  endpoint,
  isOpen,
  handleClose,
  refetch,
}: {
  endpoint: LeadProviderEndpoint;
  isOpen: boolean;
  handleClose: () => void;
  refetch: ReturnType<typeof useQuery>['refetch'];
}) => {
  const setActive = endpoint.inboundWebhookEndpoint.status === 'inactive';

  const successToast = useToast({
    ToastComponent: setActive ? SuccessToast : Toast,
    message: setActive
      ? `'${endpoint.inboundWebhookEndpoint.name}' activated.`
      : `'${endpoint.inboundWebhookEndpoint.name}' deactivated.`,
  });
  const errorToast = useToast({
    ToastComponent: ErrorToast,
    message: 'Something went wrong. Please refresh the page and try again.',
  });

  const {form, submitting, handleSubmit} = useUpdateLeadProviderEndpointMutation({
    endpoint,
    onSuccess: () => {
      successToast.handleToast();
      handleClose();
      refetch();
    },
    onError: (errors) => {
      errorToast.handleToast();
      console.error({errors});
    },
  });
  useEffect(() => {
    form.setFieldValue('updateLeadProviderEndpointForm.status', setActive ? 'active' : 'inactive');
  }, [setActive]);

  return (
    <ActionModal
      color={setActive ? colors.blue.interactive : colors.orange.status}
      icon={setActive ? Icon.CirclePlus : Icon.ExclamationTriangle}
      title={
        setActive
          ? `Activate '${endpoint.inboundWebhookEndpoint.name}'?`
          : `Deactivate '${endpoint.inboundWebhookEndpoint.name}'?`
      }
      message={
        setActive
          ? 'Leads from this provider will appear in Supermove while the provider is activated.'
          : 'Leads from this provider won’t appear in Supermove while the provider is deactivated.'
      }
      isOpen={isOpen}
      handlePrimaryAction={handleSubmit}
      handleSecondaryAction={handleClose}
      primaryActionText={'Confirm'}
      secondaryActionText={'Cancel'}
      isSubmitting={submitting}
    />
  );
};

UpdateLeadProviderEndpointStatusModal.fragment = gql`
  ${useUpdateLeadProviderEndpointMutation.fragment}
  fragment UpdateLeadProviderEndpointStatusModal on LeadProviderEndpoint {
    ...useUpdateLeadProviderEndpointMutation
  }
`;

export default UpdateLeadProviderEndpointStatusModal;
